import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "@/lib/toast";
import { getPersonalEvents, deletePersonalEvent } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { PERSONAL_EVENT_TYPES } from "@/lib/constants";
import { sortByEventDate, isEventDone } from "../utils/agendaDates";
import AgendaEventCard from "./AgendaEventCard";
import AgendaTimeline from "./AgendaTimeline";

function normalizeQ(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
}

export default function PersonalAgenda({ districtId, searchQuery = "" }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirm, ConfirmUI] = useConfirm();
  const nav = useNavigate();

  const reload = () => {
    if (!districtId) return;
    setLoading(true);
    getPersonalEvents({ district_id: districtId })
      .then(setEvents)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [districtId]);

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "¿Eliminar este evento personal?",
      description: "Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    try {
      await deletePersonalEvent(id, { district_id: districtId });
      toast.success("Evento eliminado correctamente");
      reload();
    } catch (e) {
      console.error("Delete personal event error:", e?.response?.status, e?.response?.data);
      toast.error(e?.response?.data?.detail || "No se pudo eliminar el evento");
    }
  };

  const typeLabel = (v) => PERSONAL_EVENT_TYPES.find((t) => t.value === v)?.label || v;

  const q = normalizeQ(searchQuery);
  const filtered = q
    ? events.filter((e) =>
        normalizeQ(e.title).includes(q) ||
        normalizeQ(e.notes).includes(q) ||
        normalizeQ(typeLabel(e.event_type)).includes(q)
      )
    : events;
  const sortedEvents = filtered.slice().sort(sortByEventDate);

  return (
    <div className="space-y-3" data-testid="personal-agenda">
      {ConfirmUI}

      <Link
        to="/agenda/personal/new"
        className="flex items-center justify-center gap-2 bg-brand text-white rounded-[16px] h-11 font-semibold text-[13px] shadow-[0_4px_12px_rgba(27,79,156,0.2)] transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80"
        data-testid="personal-add-button"
      >
        <Plus size={15} /> Nuevo evento personal
      </Link>

      <AgendaTimeline
        items={sortedEvents}
        emptyMessage={q ? "Sin resultados" : "Sin eventos personales"}
        emptyAction={!q && (
          <Link
            to="/agenda/personal/new"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-brand dark:text-blue-400 transition-all duration-150 active:opacity-70"
          >
            <Plus size={13} /> Agregar evento
          </Link>
        )}
        loading={loading}
        renderItem={(e) => (
          <AgendaEventCard
            key={e.id}
            href={`/agenda/personal/${e.id}`}
            title={e.title}
            date={e.date}
            endDate={e.end_date}
            time={e.time}
            type="personal"
            typeLabel={typeLabel(e.event_type)}
            done={isEventDone(e)}
            onEdit={() => nav(`/agenda/personal/${e.id}/edit`)}
            onDelete={() => handleDelete(e.id)}
            testId={`personal-event-${e.id}`}
            item={e}
          />
        )}
      />
    </div>
  );
}
